import { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { cn } from "@/utils/cn";

import { WelcomeModal } from "../WelcomeModal";
import { ArtifactDrawer, ArtifactDrawerProvider } from "./ArtifactDrawer";
import { EditorialFooter } from "./EditorialFooter";
import { Masthead } from "./Masthead";
import { Sidebar } from "./Sidebar";

export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  // Chat is the one app-shaped page: it owns its own scrolling so the composer
  // can stay pinned. Everything else is a document that scrolls as a whole.
  const isAppLayout = pathname.startsWith("/chat");

  return (
    <ArtifactDrawerProvider>
    {/* h-screen (not min-h-screen) is what makes every region scroll on its
        own. While this row could grow past the viewport, `align-items:
        stretch` made the sidebar as tall as the whole document, so its
        overflow-y-auto never had anything to overflow and the document
        carried a single scrollbar for the entire app. */}
    <div className="flex h-screen overflow-hidden bg-parchment-100">
      {/* Desktop sidebar */}
      <div className="hidden lg:block">
        <Sidebar />
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-espresso-700/40" />
          <div
            // Width comes from the Sidebar itself now that it is resizable;
            // pinning it here would fight the chosen width.
            className="relative h-full w-auto shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <Sidebar onCloseMobile={() => setMobileOpen(false)} />
          </div>
        </div>
      ) : null}

      {/* `relative` anchors the artifact drawer and its scrim, which sit below
          the masthead and cover the content column only. */}
      <div className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden">
        <Masthead onOpenMobile={() => setMobileOpen(true)} />
        <main
          className={cn(
            "flex-1 min-h-0",
            // Document pages scroll here. The chat page scrolls its own
            // transcript instead, so that the composer beneath it stays put.
            isAppLayout ? "overflow-hidden" : "overflow-y-auto",
          )}
        >
          {/* The app layout gets the full width. Its scroll container owns the
              centring instead, so the scrollbar sits against the viewport edge
              rather than at the right edge of a centred 1180px column — which
              on a wide screen put it in the middle of the display. */}
          <div
            className={cn(
              isAppLayout
                ? "flex h-full w-full flex-col"
                : "mx-auto w-full max-w-masthead px-6 py-10",
            )}
          >
            <Outlet />
          </div>
        </main>
        {/* A pinned footer under an already-pinned composer is too much
            chrome, and the spec puts nothing below the composer. */}
        {isAppLayout ? null : <EditorialFooter />}
        <ArtifactDrawer />
      </div>

      {/* First-sign-in onboarding modal. Self-fetches /me/profile and
          short-circuits when the user has already been welcomed. */}
      <WelcomeModal />
    </div>
    </ArtifactDrawerProvider>
  );
}
